import { useState } from 'react';
import { useRestaurants, Restaurant } from '@/hooks/useRestaurants';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { toast } from '@/hooks/useToast';
import {
  Building2,
  ChevronDown,
  Check,
  Plus,
  X,
  Loader2,
} from 'lucide-react';

export function RestaurantSelector() {
  const {
    restaurants,
    currentRestaurant,
    isLoading,
    hasMultipleRestaurants,
    switchRestaurant,
    createRestaurant,
  } = useRestaurants();

  const [isOpen, setIsOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRestaurantName, setNewRestaurantName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleSelect = (restaurant: Restaurant) => {
    if (restaurant.id !== currentRestaurant?.id) {
      switchRestaurant(restaurant.id);
    }
    setIsOpen(false);
  };

  const handleCreateRestaurant = async () => {
    if (!newRestaurantName.trim()) {
      toast({
        title: 'Erreur',
        description: 'Veuillez entrer un nom pour le restaurant',
        variant: 'destructive',
      });
      return;
    }

    setIsCreating(true);
    try {
      const newRestaurant = await createRestaurant({ nom: newRestaurantName.trim() });

      if (newRestaurant) {
        toast({
          title: 'Restaurant créé',
          description: `${newRestaurant.nom} a été ajouté avec succès`,
          variant: 'success',
        });
        setShowAddForm(false);
        setNewRestaurantName('');
      } else {
        throw new Error('Erreur lors de la création');
      }
    } catch {
      toast({
        title: 'Erreur',
        description: 'Impossible de créer le restaurant',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-gray-500">
        <Loader2 size={16} className="animate-spin" />
        <span className="text-sm">Chargement...</span>
      </div>
    );
  }

  if (!currentRestaurant) {
    return null;
  }

  return (
    <div className="relative">
      {/* Bouton principal */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors w-full sm:w-auto bg-white border sm:border-0"
      >
        <Building2 size={18} className="text-primary flex-shrink-0" />
        <span className="font-medium text-gray-900 truncate flex-1 text-left">
          {currentRestaurant.nom}
        </span>
        {(hasMultipleRestaurants || true) && (
          <ChevronDown
            size={16}
            className={`text-gray-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Overlay pour fermer */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setIsOpen(false);
              setShowAddForm(false);
            }}
          />

          {/* Menu déroulant */}
          <div className="absolute top-full left-0 right-0 sm:right-auto mt-1 w-auto sm:w-72 bg-white rounded-lg shadow-lg border z-50">
            <div className="p-2">
              <p className="text-xs font-medium text-gray-500 px-2 py-1 uppercase">
                Mes restaurants
              </p>

              {/* Liste des restaurants */}
              <div className="max-h-60 overflow-y-auto">
                {restaurants.map((restaurant) => (
                  <button
                    key={restaurant.id}
                    onClick={() => handleSelect(restaurant)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                      restaurant.id === currentRestaurant?.id
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <Building2 size={16} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{restaurant.nom}</p>
                      {restaurant.adresse && (
                        <p className="text-xs text-gray-500 truncate">
                          {restaurant.adresse}
                        </p>
                      )}
                    </div>
                    {restaurant.id === currentRestaurant?.id && (
                      <Check size={16} className="flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>

              {/* Séparateur */}
              <div className="border-t my-2" />

              {/* Formulaire d'ajout */}
              {showAddForm ? (
                <div className="p-2 space-y-3">
                  <div>
                    <Label htmlFor="newRestaurantName" className="text-sm">
                      Nom du nouveau restaurant
                    </Label>
                    <Input
                      id="newRestaurantName"
                      value={newRestaurantName}
                      onChange={(e) => setNewRestaurantName(e.target.value)}
                      placeholder="Mon nouveau restaurant"
                      className="mt-1"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleCreateRestaurant();
                        } else if (e.key === 'Escape') {
                          setShowAddForm(false);
                          setNewRestaurantName('');
                        }
                      }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleCreateRestaurant}
                      disabled={isCreating}
                      className="flex-1"
                    >
                      {isCreating ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        'Créer'
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowAddForm(false);
                        setNewRestaurantName('');
                      }}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-primary hover:bg-primary/5 rounded-md transition-colors"
                >
                  <Plus size={16} />
                  <span className="font-medium">Ajouter un restaurant</span>
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
